import type { DragonDossier } from "../types";

function safeFileName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 60);
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export function exportJson(dossier: DragonDossier): void {
  downloadBlob(
    new Blob([JSON.stringify(dossier, null, 2)], { type: "application/json" }),
    `${safeFileName(dossier.dragon.name)}-dossier.json`
  );
}

export function dossierToMarkdown(dossier: DragonDossier): string {
  const distribution = dossier.hoard.distribution;
  return `# ${dossier.dragon.name} ${dossier.dragon.title}

_${dossier.dragon.ageClass} ${dossier.dragon.lineageLabel} · ${dossier.dragon.role}_

Seed: \`${dossier.manifest.seed}\`  
Generator: ${dossier.manifest.generatorVersion}  
Rules adapter: ${dossier.rules.adapter}

## Overview

${dossier.dragon.summary}

- **Domain:** ${dossier.dragon.domain}
- **Goal:** ${dossier.dragon.goal}
- **Weakness:** ${dossier.dragon.weakness}
- **Temperament:** ${dossier.dragon.temperament.join(", ")}
- **Challenge:** ${dossier.dragon.challenge ?? "Neutral"}

## Appearance

- **Silhouette:** ${dossier.appearance.silhouette}
- **Horns:** ${dossier.appearance.horns ?? "Unspecified"}
- **Wings:** ${dossier.appearance.wings ?? "Unspecified"}
- **Eyes:** ${dossier.appearance.eyes ?? "Unspecified"}
- **Signature feature:** ${dossier.appearance.signatureFeature ?? dossier.appearance.markings}
- **Scars:** ${dossier.appearance.battleScars ?? dossier.appearance.markings}
- **Voice:** ${dossier.appearance.voice}
- **Scent:** ${dossier.appearance.scent}

## Rule profile

- **Armor Class:** ${dossier.rules.armorClass}
- **Hit Points:** ${dossier.rules.hitPoints} (${dossier.rules.hitDice})
- **Speed:** ${dossier.rules.speed.join(", ")}
- **Immunity:** ${dossier.rules.damageImmunities.join(", ")}

${dossier.rules.actions.map((action) => `### ${action.name}\n\n${action.description}`).join("\n\n")}

## Lair — ${dossier.lair.name}

${dossier.lair.summary}

${dossier.lair.layout ? `Layout: **${dossier.lair.layout}**\n\n` : ""}${dossier.lair.mapNotes?.length ? `${dossier.lair.mapNotes.map((note) => `- ${note}`).join("\n")}\n\n` : ""}

${dossier.lair.rooms
  .map(
    (room) =>
      `### ${room.name}\n\n- Role: ${room.role}\n- Danger: ${room.danger}\n- Occupants: ${room.occupants}\n- Hazards: ${room.hazards.join(", ")}\n- Treasure: ${room.treasure}\n\n${room.tacticalNote}`
  )
  .join("\n\n")}

## Hoard

Estimated value: **${dossier.hoard.totalValue.toLocaleString("en-US")} gp**

- Visible: ${distribution.visible}%
- Hidden: ${distribution.hidden}%
- Claimed: ${distribution.claimed}%
- Cursed or trapped: ${distribution.cursed}%

${dossier.hoard.items
  .map(
    (item) =>
      `### ${item.name}\n\n${item.rarity}; ${item.value.toLocaleString("en-US")} gp. Claimed by ${item.claimedBy}.\n\n${item.origin} ${item.complication}\n\n**Hook:** ${item.hook}`
  )
  .join("\n\n")}

## Factions

${dossier.factions
  .map((faction) => `- **${faction.name}** (${faction.kind}, ${faction.relation}): ${faction.goal}`)
  .join("\n")}

## History

${dossier.history
  .map((event) => {
    const links = event.linkedEntities?.length
      ? event.linkedEntities.map((link) => `${link.type}: ${link.name}`).join("; ")
      : event.linkedEntity;
    return `- **${event.era ? `${event.era}, ` : ""}Age ${event.age} - ${event.title}:** ${event.description}${event.consequence ? ` Consequence: ${event.consequence}` : ""}${event.witness ? ` Witness: ${event.witness}.` : ""} _Linked: ${links}_`;
  })
  .join("\n")}

## Encounter script

${dossier.encounterScript
  .map((phase) => `### ${phase.title}\n\n${phase.steps.map((step) => `- ${step}`).join("\n")}`)
  .join("\n\n")}

## Adventure hooks

${dossier.hooks
  .map((hook) => `### ${hook.title}\n\n${hook.premise}\n\n**Stakes:** ${hook.stakes}`)
  .join("\n\n")}

---

Generated by Tiamat.nl. Original, unofficial material using an SRD-compatible structure.
`;
}

export function exportMarkdown(dossier: DragonDossier): void {
  downloadBlob(
    new Blob([dossierToMarkdown(dossier)], { type: "text/markdown" }),
    `${safeFileName(dossier.dragon.name)}-dossier.md`
  );
}

export function serializeSvg(svg: SVGSVGElement): string {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  return new XMLSerializer().serializeToString(clone);
}

export function exportSvg(svg: SVGSVGElement, dossier: DragonDossier): void {
  downloadBlob(
    new Blob([serializeSvg(svg)], { type: "image/svg+xml" }),
    `${safeFileName(dossier.dragon.name)}-lair.svg`
  );
}

export async function exportPng(svg: SVGSVGElement, dossier: DragonDossier): Promise<void> {
  const serialized = serializeSvg(svg);
  const image = new Image();
  const source = URL.createObjectURL(new Blob([serialized], { type: "image/svg+xml" }));
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error("The SVG map could not be rendered."));
    image.src = source;
  });
  const canvas = document.createElement("canvas");
  canvas.width = 1600;
  canvas.height = 1000;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas export is unavailable.");
  context.fillStyle = "#d9c79d";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(source);
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((value) => (value ? resolve(value) : reject(new Error("PNG export failed."))), "image/png")
  );
  downloadBlob(blob, `${safeFileName(dossier.dragon.name)}-lair.png`);
}
